// Composition root for shell-app browser logging.
//
// The implementation lives in @syia-ai/observability; this file binds only the
// app-specific parts (endpoint, service name, session source, error-field
// mapping) so call sites import stable local names and never the package
// directly — a package swap stays a one-file change.

import {
  initClientLogger as initSharedClientLogger,
  logAccess as sharedLogAccess,
  logEvent,
} from '@syia-ai/observability';
import { ApiError } from '@syia-ai/contracts';
import { getSessionIdFromToken } from '@syia-ai/jwt';
import { getAuthToken } from '@/utils/authToken';
import { config, isProduction } from '@/config/runtime';

const FLUSH_ENDPOINT = '/api/client-logs';
const SERVICE = 'shell-app';

// Cached: the token is stable for the tab's lifetime and getSessionId() runs on
// every event. `anon.<epoch>` keeps pre-login events correlatable to each other
// even though they cannot be tied to a user.
//
// The shell needs that anonymous path far more than a normal App does: it is the
// only surface an unauthenticated visitor ever reaches, so the sign-in failures
// most worth seeing all happen before there is a token to derive an id from.
let cachedSessionId = '';

const anonSessionId = (): string => `anon.${Math.floor(Date.now() / 1000)}`;

const getSessionId = (): string => {
  if (cachedSessionId) return cachedSessionId;
  try {
    const token = getAuthToken();
    // getSessionIdFromToken returns null for a token carrying no user id; the
    // anon fallback keeps this a guaranteed string, because a missing sessionId
    // silently drops the event from every session-scoped CloudWatch query.
    cachedSessionId = (token && getSessionIdFromToken(token)) || anonSessionId();
  } catch {
    // A malformed token must never break logging — logging failures are the one
    // class of error that cannot report itself.
    cachedSessionId = anonSessionId();
  }
  return cachedSessionId;
};

/**
 * ApiError carries the fields that make a failure diagnosable (code, status,
 * retryable) on its own shape rather than inside the message, so it is unpacked
 * explicitly — the generic Error path would flatten them into prose.
 */
const errorFields = (error: unknown): Record<string, unknown> => {
  if (error instanceof ApiError) {
    return {
      code: error.code,
      status: error.status,
      retryable: error.retryable,
      msg: error.message,
    };
  }
  if (error instanceof Error) {
    return {
      msg: error.message,
      err: { name: error.name, message: error.message, stack: error.stack },
    };
  }
  return { msg: String(error) };
};

/**
 * Call once at app mount. SSR-safe and idempotent.
 *
 * `buildId` is read here, in app code, rather than via the package's own process.env
 * fallback: there is no `process` in the browser at all now, and the value reaches the
 * page as runtime config rather than being compiled in. See src/config/runtime.ts.
 */
export const initClientLogger = (): void => {
  initSharedClientLogger({
    endpoint: FLUSH_ENDPOINT,
    service: SERVICE,
    // Production always ships. SHIP_CLIENT_LOGS=true opts a local dev session in, so the
    // full browser -> /api/client-logs -> stdout chain can be exercised by setting an env
    // var rather than editing this file and remembering to revert it before committing.
    //
    // isProduction is the DEPLOYMENT's environment, not the bundle's compile mode. The two
    // differ for a production bundle served by a dev server, which is exactly when you
    // want to know whether logs are shipping.
    ship: isProduction || config.shipClientLogs,
    buildId: config.buildId,
    getSessionId,
  });
};

/** User-intent event at a feature entry point. Once per trigger, not per render. */
export const logAccess = (event: string, context: Record<string, unknown> = {}): void =>
  sharedLogAccess(event, context);

/**
 * Call inside non-fallback catch blocks. Technical detail belongs here; the
 * user-facing message belongs in the UI state.
 */
export const reportError = (error: unknown, context: Record<string, unknown> = {}): void =>
  logEvent({ event: 'app_error', level: 'error', ...context, ...errorFields(error) });
